/**
 * Phase 6 Component 27 — Report framework registration convention.
 *
 * Each report module exports a `ReportDefinition` via `defineReport()`. The
 * framework collects all definitions at boot time and registers them in the
 * `report_definitions` DB table. The catalogue UI reads from the table; the
 * run-now path looks up the in-memory definition by `report_code`.
 *
 * A report definition has three pieces:
 *
 *   1. **Metadata**: code, title, category, parameter schema, output formats,
 *      allowed roles, drilldown spec.
 *   2. **Parameter schema** (Zod): validates the run parameters before
 *      executing the data fn. Persists the parsed schema as JSON to
 *      `report_definitions.parameter_schema_json`.
 *   3. **Data fn**: pure function `(ctx) => Promise<ReportRow[]>`. Receives
 *      a tenant-scoped data context (DB client + tenant_id + parsed params)
 *      and returns rows matching the columns spec.
 *
 * Reports are PURE in the data layer; tenant + auth scoping is enforced at
 * the runner layer (apps/web/src/lib/reports/runner.ts).
 */

/**
 * Generic validator interface — caller plugs in Zod (or any other validator)
 * via this minimal contract. The framework doesn't depend on zod directly so
 * @statura/utils can stay light.
 */
export interface ParamsValidator<T> {
  parse(input: unknown): T;
  safeParse?(input: unknown): { success: boolean; data?: T; error?: unknown };
}

export type ReportCategory =
  | 'OPERATIONAL'
  | 'COST'
  | 'MARGIN'
  | 'PRODUCTIVITY'
  | 'FW_COMPLIANCE'
  | 'SCHADS_COMPLIANCE'
  | 'QUALITY'
  | 'PEOPLE'
  | 'CLINICAL'
  | 'EXECUTIVE';

export type OutputFormat = 'CSV' | 'XLSX' | 'PDF' | 'JSON';

export type ReportFirewallFamily = 'SAH' | 'RAC' | 'OTHER' | 'SHARED';

export interface ReportProgramContext {
  providerType: 'SAH' | 'RAC' | 'MIXED' | 'OTHER' | 'UNKNOWN';
  resolutionStatus: 'RESOLVED_SINGLE' | 'RESOLVED_DUAL' | 'RESOLVED_MULTI' | 'UNKNOWN';
  activeProgramFamily: string | null;
}

export interface ColumnSpec {
  /** Object key in the row */
  key: string;
  /** Display label (column header) */
  label: string;
  /** Data type drives formatting + alignment */
  type: 'string' | 'number' | 'currency' | 'date' | 'datetime' | 'boolean' | 'duration_minutes';
  /** Decimal places displayed in numeric XLSX columns; omitted keeps integer formatting. */
  decimalPlaces?: 0 | 1 | 2 | 3 | 4;
  /** Optional drill-down — if set, table cell becomes a link */
  drilldown?: {
    href: (row: Record<string, unknown>) => string;
    label?: string;
  };
  /** PHI flag — if true, column is redacted in scheduled emails when redact_phi=true */
  isPhi?: boolean;
  /** Sortable in UI */
  sortable?: boolean;
}

export interface ReportRunContext<TParams = unknown> {
  tenantId: string;
  /** Exact governed family selected and revalidated by the runner. */
  programFamily: ReportFirewallFamily;
  parsedParams: TParams;
  /** Generic DB client — supplied by the runner. Never queried directly here. */
  db: unknown;
  /** Optional logger for diagnostic output */
  logger?: { info: (msg: string) => void; warn: (msg: string) => void };
}

export interface ReportDefinition<TParams = unknown> {
  reportCode: string;
  title: string;
  description: string;
  category: ReportCategory;
  /** Validator for params (typically a Zod schema injected by the caller). */
  paramsSchema: ParamsValidator<TParams>;
  outputFormats: OutputFormat[];
  allowedRoles: string[];
  /**
   * Presentation/data firewall families for this report. `SHARED` is reserved
   * for genuinely programme-neutral output. Family-scoped reports receive the
   * exact selected family in ReportRunContext and must enforce it in the query.
   */
  firewallFamilies: ReportFirewallFamily[];
  /**
   * The query binds every programme-sensitive row to the selected programme.
   * Required before a DUAL/MULTI provider may run the report through a lens.
   */
  supportsSelectedProgramContext?: boolean;
  schedulable: boolean;
  columns: ColumnSpec[];
  /** Optional, declarative output customisation. Reports opt in explicitly. */
  customisation?: {
    filters: Array<{
      key: string;
      label: string;
      source: 'staff' | 'facilities' | 'static';
      options?: Array<{ value: string; label: string }>;
    }>;
    filterNote?: string;
    summaryNote?: string;
    mandatoryColumns: string[];
    groupKeys: Array<{ key: string; label: string }>;
    modes: Array<'detail' | 'summary' | 'both'>;
  };
  /** Optional drill-down spec metadata persisted to DB */
  drilldownSpec?: Record<string, unknown>;
  /** Pure data fn — runner injects context */
  fetchRows: (ctx: ReportRunContext<TParams>) => Promise<Record<string, unknown>[]>;
  /**
   * Optional coverage notices shown with the run, such as paid figures still
   * pending for part of the range. Runs on the same db as fetchRows; a failed
   * check fails the run rather than letting a partial figure look complete.
   */
  fetchNotices?: (ctx: ReportRunContext<TParams>) => Promise<string[]>;
}

const REGISTRY = new Map<string, ReportDefinition>();

/**
 * Register a report definition. Throws if the report_code is already in use.
 */
export function defineReport<T>(def: ReportDefinition<T>): ReportDefinition<T> {
  if (REGISTRY.has(def.reportCode)) {
    throw new Error(`Report code "${def.reportCode}" is already registered.`);
  }
  REGISTRY.set(def.reportCode, def as ReportDefinition);
  return def;
}

/** Register a report whose semantics and rows are programme-neutral. */
export function defineSharedReport<T>(
  def: Omit<ReportDefinition<T>, 'firewallFamilies'>,
): ReportDefinition<T> {
  return defineReport({ ...def, firewallFamilies: ['SHARED'] });
}

/**
 * Fail-closed programme predicate shared by catalogue, direct run,
 * subscriptions, scheduled dispatch and artifact access.
 */
export function isReportAvailableForProgram(
  definition: Pick<ReportDefinition, 'firewallFamilies' | 'supportsSelectedProgramContext'>,
  context: ReportProgramContext,
): boolean {
  return resolveReportProgramFamily(definition, context) !== null;
}

/**
 * Resolve the exact family a report query must enforce. Mixed providers are
 * eligible only after a trusted lens resolves providerType and
 * activeProgramFamily to the same family; unresolved dual/multi contexts stay
 * fail-closed. Scheduled callers must persist and revalidate this value.
 */
export function resolveReportProgramFamily(
  definition: Pick<ReportDefinition, 'firewallFamilies' | 'supportsSelectedProgramContext'>,
  context: ReportProgramContext,
): ReportFirewallFamily | null {
  if (definition.firewallFamilies.includes('SHARED')) return 'SHARED';
  if (context.resolutionStatus === 'UNKNOWN' || !context.activeProgramFamily) return null;
  const providerFamily = context.providerType;
  if (providerFamily !== 'SAH' && providerFamily !== 'RAC' && providerFamily !== 'OTHER') {
    return null;
  }
  if (
    (context.resolutionStatus === 'RESOLVED_DUAL' ||
      context.resolutionStatus === 'RESOLVED_MULTI') &&
    definition.supportsSelectedProgramContext !== true
  ) {
    return null;
  }
  return definition.firewallFamilies.includes(providerFamily) ? providerFamily : null;
}

/** Artifact bytes must retain the exact family that governed their query. */
export function isReportArtifactAvailableForProgram(
  definition: Pick<ReportDefinition, 'firewallFamilies' | 'supportsSelectedProgramContext'>,
  context: ReportProgramContext,
  parameters: Record<string, unknown> | null | undefined,
): boolean {
  const currentFamily = resolveReportProgramFamily(definition, context);
  if (!currentFamily) return false;
  return parameters?.__programFamily === currentFamily;
}

/** Look up a registered report by its code. Returns undefined when not found. */
export function getReport(reportCode: string): ReportDefinition | undefined {
  return REGISTRY.get(reportCode);
}

/** All registered reports — primary input to catalogue UI sync. */
export function listReports(): ReportDefinition[] {
  return Array.from(REGISTRY.values());
}

/** Test-only: clear the registry. */
export function _clearRegistryForTests(): void {
  REGISTRY.clear();
}
